import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { toast } from 'react-hot-toast';
import {
  GET_PROPERTIES_FOR_QB_ASSIGNMENT,
  GET_QUICKBOOKS_CUSTOMERS,
} from '../../graphql/quickbooks';
import { UPDATE_PROPERTY, GET_USERS } from '../../graphql/queries';
import PropertyManagementFilter from '../properties/PropertyManagementFilter';
import { parseManagementFilter } from '../../utils/propertyManagers';

const ITEMS_PER_PAGE = 25;

const FILTER_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'unassigned', label: 'Unassigned' },
];

const AREA_FILTER_OPTIONS = [
  { id: 'all', label: 'All Areas', areas: [] },
  { id: 'kiawah-seabrook', label: 'Kiawah + Seabrook', areas: ['KIAWAH', 'SEABROOK_ISLAND'] },
  { id: 'iop', label: 'IOP + Wild Dunes', areas: ['ISLE_OF_PALMS', 'ISLE_OF_PALMS_WILD_DUNES'] },
];

function QuickBooksCustomerAssignments() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeAreaFilter, setActiveAreaFilter] = useState('all');
  const [selectedPropertyManagerId, setSelectedPropertyManagerId] = useState('');
  const [savingPropertyId, setSavingPropertyId] = useState(null);
  const [savedPropertyId, setSavedPropertyId] = useState(null);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(debounceTimerRef.current);
  }, [searchTerm]);

  const offset = (currentPage - 1) * ITEMS_PER_PAGE;

  const selectedAreas = AREA_FILTER_OPTIONS.find(o => o.id === activeAreaFilter)?.areas || [];

  const { data: propertiesData, loading: propertiesLoading, refetch: refetchProperties } = useQuery(
    GET_PROPERTIES_FOR_QB_ASSIGNMENT,
    {
      variables: {
        limit: ITEMS_PER_PAGE,
        offset,
        search: debouncedSearch || undefined,
        quickbooksFilter: activeFilter === 'all' ? undefined : activeFilter,
        areas: selectedAreas.length > 0 ? selectedAreas : undefined,
        ...parseManagementFilter(selectedPropertyManagerId),
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const { data: customersData, loading: customersLoading, error: customersError, refetch: refetchCustomers } = useQuery(GET_QUICKBOOKS_CUSTOMERS, {
    fetchPolicy: 'network-only',
  });

  const { data: usersData } = useQuery(GET_USERS);

  const [updateProperty] = useMutation(UPDATE_PROPERTY);

  const properties = propertiesData?.properties?.items || [];
  const totalItems = propertiesData?.properties?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const customers = customersData?.quickBooksCustomers || [];
  const activeCustomers = [...customers].sort((a, b) => {
    if (a.active !== b.active) {
      return a.active ? -1 : 1;
    }
    return a.displayName.localeCompare(b.displayName);
  });

  const propertyManagers = (usersData?.users || [])
    .filter(u => ['PROPERTY_MANAGER', 'ADMIN', 'GUEST_APP_MANAGER'].includes(u.role) && u.isActive !== false)
    .sort((a, b) => {
      const nameA = a.name || `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email;
      const nameB = b.name || `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.email;
      return nameA.localeCompare(nameB);
    });

  const assignedCount = properties.filter(p => p.quickbooksCustomerId).length;

  const handleCustomerChange = useCallback(async (propertyId, quickbooksCustomerId) => {
    const newValue = quickbooksCustomerId || null;
    setSavingPropertyId(propertyId);
    setSavedPropertyId(null);
    try {
      await updateProperty({
        variables: {
          id: propertyId,
          input: { quickbooksCustomerId: newValue },
        },
        optimisticResponse: {
          updateProperty: {
            __typename: 'Property',
            ...properties.find(p => p.id === propertyId),
            quickbooksCustomerId: newValue,
          },
        },
        update: (cache, { data }) => {
          if (!data?.updateProperty) return;
          const queryVars = {
            limit: ITEMS_PER_PAGE,
            offset,
            search: debouncedSearch || undefined,
            quickbooksFilter: activeFilter === 'all' ? undefined : activeFilter,
            areas: selectedAreas.length > 0 ? selectedAreas : undefined,
            ...parseManagementFilter(selectedPropertyManagerId),
          };
          const existing = cache.readQuery({
            query: GET_PROPERTIES_FOR_QB_ASSIGNMENT,
            variables: queryVars,
          });
          if (!existing?.properties?.items) return;
          cache.writeQuery({
            query: GET_PROPERTIES_FOR_QB_ASSIGNMENT,
            variables: queryVars,
            data: {
              properties: {
                ...existing.properties,
                items: existing.properties.items.map(p =>
                  p.id === propertyId
                    ? { ...p, quickbooksCustomerId: data.updateProperty.quickbooksCustomerId }
                    : p
                ),
              },
            },
          });
        },
      });
      setSavedPropertyId(propertyId);
      setTimeout(() => setSavedPropertyId(prev => prev === propertyId ? null : prev), 2000);
    } catch (err) {
      toast.error(`Failed to update: ${err.message}`);
    } finally {
      setSavingPropertyId(null);
    }
  }, [updateProperty, properties, offset, debouncedSearch, activeFilter, selectedAreas, selectedPropertyManagerId]);

  const handleFilterChange = useCallback((filterId) => {
    setActiveFilter(filterId);
    setCurrentPage(1);
  }, []);

  const handleAreaFilterChange = useCallback((filterId) => {
    setActiveAreaFilter(filterId);
    setCurrentPage(1);
  }, []);

  const handlePropertyManagerChange = useCallback((valueOrEvent) => {
    const value = typeof valueOrEvent === 'string' ? valueOrEvent : valueOrEvent.target.value;
    setSelectedPropertyManagerId(value);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearch('');
    setCurrentPage(1);
  }, []);

  const getCustomerDisplayName = useCallback((customerId) => {
    if (!customerId) return null;
    const customer = customers.find(c => c.id === customerId);
    return customer?.displayName || customerId;
  }, [customers]);

  const isLoading = propertiesLoading || customersLoading;
  const showStart = Math.min(offset + 1, totalItems);
  const showEnd = Math.min(offset + ITEMS_PER_PAGE, totalItems);

  return (
    <div className="mt-8 pt-8 border-t border-gray-200">
      <div className="mb-6">
        <h3 className="text-base font-medium text-gray-900">Property Customer Assignments</h3>
        <p className="mt-1 text-sm text-gray-500">
          Assign QuickBooks customers to properties for invoicing
        </p>
      </div>

      {customersError && (
        <div className="mb-4 rounded-md bg-red-50 p-4 border border-red-200">
          <p className="text-sm font-medium text-red-800">Could not load QuickBooks customers</p>
          <p className="mt-1 text-sm text-red-700">{customersError.message}</p>
          <button
            type="button"
            onClick={() => refetchCustomers()}
            className="mt-2 text-sm font-medium text-red-800 underline hover:text-red-900"
          >
            Retry
          </button>
          <p className="mt-2 text-xs text-red-600">
            {customersError.message?.includes('401') || customersError.message?.toLowerCase().includes('authorization')
              ? 'QuickBooks rejected the stored connection. Go to Settings → QuickBooks, click Disconnect, then Connect again.'
              : 'If this persists, reconnect QuickBooks in Settings or check backend logs for the detailed API error.'}
          </p>
        </div>
      )}

      {/* Summary Bar */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="inline-flex items-center rounded-full bg-sky-50 px-3 py-1 text-sm font-medium text-sky-700 ring-1 ring-inset ring-sky-600/20">
            {totalItems} {totalItems === 1 ? 'property' : 'properties'}
          </span>
          {activeFilter === 'all' && !debouncedSearch && (
            <span className="text-sm text-gray-500">
              — page shows {assignedCount} of {properties.length} assigned
            </span>
          )}
        </div>
      </div>

      {/* Property Manager Filter */}
      <div className="mb-4">
        <label htmlFor="pm-filter" className="block text-sm font-medium text-gray-700 mb-1">Property Manager</label>
        <PropertyManagementFilter
          id="pm-filter"
          value={selectedPropertyManagerId}
          onChange={handlePropertyManagerChange}
          users={propertyManagers}
          className="block w-full sm:w-72 rounded-md border border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
        />
      </div>

      {/* Search & Filter Controls */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search properties by name, address, city, or state..."
            className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-10 text-sm placeholder-gray-400 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            aria-label="Search properties"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
              tabIndex={0}
            >
              <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <div className="flex rounded-md shadow-sm" role="group" aria-label="Filter by assignment">
          {FILTER_OPTIONS.map((option) => (
            <button
              key={option.id}
              onClick={() => handleFilterChange(option.id)}
              className={`
                px-4 py-2 text-sm font-medium border
                first:rounded-l-md last:rounded-r-md
                focus:z-10 focus:outline-none focus:ring-1 focus:ring-sky-500
                ${activeFilter === option.id
                  ? 'bg-sky-50 text-sky-700 border-sky-300 z-10'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}
                ${option.id !== 'all' ? '-ml-px' : ''}
              `}
              aria-pressed={activeFilter === option.id}
              tabIndex={0}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="flex rounded-md shadow-sm" role="group" aria-label="Filter by area">
          {AREA_FILTER_OPTIONS.map((option) => (
            <button
              key={option.id}
              onClick={() => handleAreaFilterChange(option.id)}
              className={`
                px-4 py-2 text-sm font-medium border
                first:rounded-l-md last:rounded-r-md
                focus:z-10 focus:outline-none focus:ring-1 focus:ring-sky-500
                ${activeAreaFilter === option.id
                  ? 'bg-sky-50 text-sky-700 border-sky-300 z-10'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}
                ${option.id !== 'all' ? '-ml-px' : ''}
              `}
              aria-pressed={activeAreaFilter === option.id}
              tabIndex={0}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Property
                </th>
                <th scope="col" className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Area
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  QuickBooks Customer
                </th>
                <th scope="col" className="hidden sm:table-cell px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 w-16">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading && properties.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 text-gray-400 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm text-gray-500">Loading properties...</span>
                    </div>
                  </td>
                </tr>
              ) : properties.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center">
                    <p className="text-sm text-gray-500">
                      {debouncedSearch
                        ? `No properties found matching "${debouncedSearch}"`
                        : activeFilter === 'assigned'
                          ? 'No properties have a QuickBooks customer assigned'
                          : activeFilter === 'unassigned'
                            ? 'All properties have a QuickBooks customer assigned'
                            : 'No properties found'}
                    </p>
                  </td>
                </tr>
              ) : (
                properties.map((property) => (
                  <PropertyRow
                    key={property.id}
                    property={property}
                    activeCustomers={activeCustomers}
                    isSaving={savingPropertyId === property.id}
                    isSaved={savedPropertyId === property.id}
                    onCustomerChange={handleCustomerChange}
                    getCustomerDisplayName={getCustomerDisplayName}
                    customersLoading={customersLoading}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {showStart}–{showEnd} of {totalItems} {totalItems === 1 ? 'property' : 'properties'}
          </p>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous page"
              tabIndex={0}
            >
              <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
              </svg>
              Prev
            </button>
            <PageButtons currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next page"
              tabIndex={0}
            >
              Next
              <svg className="h-4 w-4 ml-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PropertyRow({ property, activeCustomers, isSaving, isSaved, onCustomerChange, getCustomerDisplayName, customersLoading }) {
  const hasAssignment = Boolean(property.quickbooksCustomerId);
  const selectedCustomerName = getCustomerDisplayName(property.quickbooksCustomerId);

  return (
    <tr className={`hover:bg-gray-50 transition-colors ${!property.isActive ? 'opacity-60' : ''}`}>
      <td className="px-4 py-3">
        <div className="flex items-center">
          <div>
            <p className="text-sm font-medium text-gray-900">{property.name}</p>
            {!property.isActive && (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 mt-0.5">
                Archived
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="hidden sm:table-cell px-4 py-3">
        <span className="text-sm text-gray-600">{property.area || '—'}</span>
      </td>
      <td className="px-4 py-3">
        <CustomerSearchCombobox
          customers={activeCustomers}
          selectedCustomerId={property.quickbooksCustomerId}
          selectedCustomerName={selectedCustomerName}
          onSelect={(customerId) => onCustomerChange(property.id, customerId)}
          disabled={isSaving || customersLoading}
          hasAssignment={hasAssignment}
          propertyName={property.name}
        />
      </td>
      <td className="hidden sm:table-cell px-4 py-3 text-center">
        {isSaving ? (
          <svg className="animate-spin h-4 w-4 text-sky-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : isSaved ? (
          <svg className="h-5 w-5 text-green-500 mx-auto" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-label="Saved">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.06l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
          </svg>
        ) : hasAssignment ? (
          <span className="inline-flex h-2.5 w-2.5 rounded-full bg-green-400 mx-auto" title="Assigned" />
        ) : (
          <span className="inline-flex h-2.5 w-2.5 rounded-full bg-orange-400 mx-auto" title="Unassigned" />
        )}
      </td>
    </tr>
  );
}

const MAX_VISIBLE_RESULTS = 8;

function customerMatchesQuery(customer, rawQuery) {
  const q = rawQuery.toLowerCase().trim();
  if (!q) {
    return true;
  }
  const fields = [
    customer.displayName,
    customer.companyName,
    customer.givenName,
    customer.familyName,
    customer.email,
    customer.address,
    customer.city,
    customer.state,
    customer.zip,
  ];
  return fields.some((field) => field && String(field).toLowerCase().includes(q));
}

function CustomerSearchCombobox({ customers, selectedCustomerId, selectedCustomerName, onSelect, disabled, hasAssignment, propertyName }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const searchDebounceRef = useRef(null);

  const [searchQuickBooksCustomers, { data: searchData, loading: searchLoading }] = useLazyQuery(
    GET_QUICKBOOKS_CUSTOMERS,
    { fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    if (!query.trim() || query.trim().length < 2) {
      return undefined;
    }
    searchDebounceRef.current = setTimeout(() => {
      searchQuickBooksCustomers({
        variables: { search: query.trim(), maxResults: 100 },
      });
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [query, searchQuickBooksCustomers]);

  const serverMatches = searchData?.quickBooksCustomers || [];
  const localMatches = query.trim() ? customers.filter((c) => customerMatchesQuery(c, query)) : customers;
  const mergedById = new Map();
  [...localMatches, ...serverMatches].forEach((c) => mergedById.set(c.id, c));
  const filteredCustomers = query.trim()
    ? Array.from(mergedById.values()).sort((a, b) => {
        if (a.active !== b.active) {
          return a.active ? -1 : 1;
        }
        return a.displayName.localeCompare(b.displayName);
      })
    : customers;

  const visibleCustomers = filteredCustomers.slice(0, MAX_VISIBLE_RESULTS);
  const hasMoreResults = filteredCustomers.length > MAX_VISIBLE_RESULTS;

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [disabled]);

  const handleSelectCustomer = useCallback((customerId) => {
    onSelect(customerId);
    setIsOpen(false);
    setQuery('');
  }, [onSelect]);

  const handleClear = useCallback((e) => {
    e.stopPropagation();
    onSelect('');
  }, [onSelect]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, visibleCustomers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && visibleCustomers[highlightedIndex]) {
      e.preventDefault();
      handleSelectCustomer(visibleCustomers[highlightedIndex].id);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  }, [visibleCustomers, highlightedIndex, handleSelectCustomer]);

  if (isOpen) {
    return (
      <div ref={containerRef} className="relative w-full max-w-xs">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
            <svg className="h-3.5 w-3.5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type to search customers..."
            className="block w-full rounded-md border border-sky-400 py-1.5 pl-8 pr-3 text-sm placeholder-gray-400 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            aria-label={`Search QuickBooks customer for ${propertyName}`}
            aria-expanded="true"
            role="combobox"
            aria-autocomplete="list"
          />
        </div>
        <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-60 overflow-y-auto">
          {searchLoading && query.trim().length >= 2 && (
            <div className="px-3 py-2 text-xs text-gray-400">Searching QuickBooks...</div>
          )}
          {visibleCustomers.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              {query ? `No customers matching "${query}"` : 'No customers available'}
            </div>
          ) : (
            <>
              {selectedCustomerId && (
                <button
                  onClick={() => handleSelectCustomer('')}
                  className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 border-b border-gray-100 flex items-center"
                  tabIndex={0}
                >
                  <svg className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                  Remove assignment
                </button>
              )}
              {visibleCustomers.map((customer, idx) => (
                <button
                  key={customer.id}
                  onClick={() => handleSelectCustomer(customer.id)}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  className={`
                    w-full px-3 py-2 text-left text-sm flex items-center justify-between
                    ${idx === highlightedIndex ? 'bg-sky-50 text-sky-900' : 'text-gray-900 hover:bg-gray-50'}
                    ${customer.id === selectedCustomerId ? 'font-medium' : ''}
                  `}
                  role="option"
                  aria-selected={customer.id === selectedCustomerId}
                  tabIndex={-1}
                >
                  <span className="truncate">
                    {customer.displayName}
                    {!customer.active && (
                      <span className="text-amber-600 font-normal"> (inactive)</span>
                    )}
                    {customer.companyName && (
                      <span className="text-gray-500 font-normal"> ({customer.companyName})</span>
                    )}
                  </span>
                  {customer.id === selectedCustomerId && (
                    <svg className="h-4 w-4 text-sky-600 flex-shrink-0 ml-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
              {hasMoreResults && (
                <div className="px-3 py-2 text-xs text-gray-400 border-t border-gray-100">
                  {filteredCustomers.length - MAX_VISIBLE_RESULTS} more — type to narrow results
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={handleOpen}
      disabled={disabled}
      className={`
        group flex items-center justify-between w-full max-w-xs rounded-md border py-1.5 pl-3 pr-2 text-sm text-left
        focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500
        disabled:opacity-50 disabled:cursor-not-allowed
        ${hasAssignment
          ? 'border-gray-300 text-gray-900 hover:border-gray-400'
          : 'border-orange-300 bg-orange-50 text-orange-900 hover:border-orange-400'}
      `}
      aria-label={`QuickBooks customer for ${propertyName}`}
      tabIndex={0}
    >
      <span className={`truncate ${hasAssignment ? '' : 'text-orange-400'}`}>
        {selectedCustomerName || 'Select customer...'}
      </span>
      <span className="flex items-center flex-shrink-0 ml-2">
        {hasAssignment && (
          <span
            onClick={handleClear}
            className="p-0.5 rounded text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
            role="button"
            aria-label={`Remove customer from ${propertyName}`}
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') handleClear(e); }}
          >
            <svg className="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </span>
        )}
        <svg className="h-4 w-4 text-gray-400 ml-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </span>
    </button>
  );
}

function PageButtons({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
      <PageButton key={page} page={page} isActive={page === currentPage} onClick={onPageChange} />
    ));
  }

  const pages = [];
  pages.push(1);
  if (currentPage > 3) pages.push('...');
  for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
    pages.push(i);
  }
  if (currentPage < totalPages - 2) pages.push('...');
  pages.push(totalPages);

  return pages.map((page, idx) =>
    page === '...' ? (
      <span key={`ellipsis-${idx}`} className="px-2 py-1.5 text-sm text-gray-400">…</span>
    ) : (
      <PageButton key={page} page={page} isActive={page === currentPage} onClick={onPageChange} />
    )
  );
}

function PageButton({ page, isActive, onClick }) {
  return (
    <button
      onClick={() => onClick(page)}
      className={`
        inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium min-w-[36px]
        ${isActive
          ? 'bg-sky-600 text-white border border-sky-600'
          : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}
      `}
      aria-label={`Page ${page}`}
      aria-current={isActive ? 'page' : undefined}
      tabIndex={0}
    >
      {page}
    </button>
  );
}

export default QuickBooksCustomerAssignments;
